import React, { useEffect, useMemo, useState } from "react";
import {
  FiEdit3,
  FiExternalLink,
  FiImage,
  FiLayout,
  FiSave,
  FiX,
} from "react-icons/fi";

import Drawer from "./Drawer";
import MediaPickerModal from "./MediaPickerModal";
import { toAbsolutePublicUrl } from "../../../../services/mediaLibraryServices";

const inputClass =
  "w-full rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-sky-950";

const textareaClass =
  "w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm leading-7 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-sky-950";

const slugify = (s = "") =>
  String(s)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 255);

const toLocalDateTime = (iso) => {
  if (!iso) return "";

  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const initialFormState = {
  locale: "fr",
  title: "",
  subtitle: "",
  slug: "",
  excerpt: "",
  cover_image_url: "",
  parent_id: null,
  template: "",
  redirect_url: "",
  seo_title: "",
  seo_description: "",
  seo_keywords: "",
  is_active: true,
  published_at: "",
};

export default function PageForm({
  open,
  onClose,
  onSave,
  initial = null,
  parents = [],
  onOpenBuilder,
}) {
  const isEdit = !!initial?.id;

  const [form, setForm] = useState(initialFormState);
  const [autoSlug, setAutoSlug] = useState(true);
  const [savingMode, setSavingMode] = useState("");
  const [error, setError] = useState("");
  const [mediaOpen, setMediaOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    setError("");

    if (initial) {
      setForm({
        locale: initial.locale || "fr",
        title: initial.title || "",
        subtitle: initial.subtitle || "",
        slug: initial.slug || "",
        excerpt: initial.excerpt || "",
        cover_image_url: initial.cover_image_url || "",
        parent_id: initial.parent_id ?? null,
        template: initial.template || "",
        redirect_url: initial.redirect_url || "",
        seo_title: initial.seo_title || "",
        seo_description: initial.seo_description || "",
        seo_keywords: initial.seo_keywords || "",
        is_active: !!initial.is_active,
        published_at: toLocalDateTime(initial.published_at),
      });

      setAutoSlug(false);
    } else {
      setForm(initialFormState);
      setAutoSlug(true);
    }
  }, [open, initial]);

  useEffect(() => {
    if (!autoSlug) return;

    setForm((current) => ({
      ...current,
      slug: slugify(current.title),
    }));
  }, [form.title, autoSlug]);

  const parentOptions = useMemo(() => {
    const root = [{ id: "", title: "Aucune page parente / Racine" }];

    const sorted = [...parents].sort((a, b) =>
      String(a.title || "").localeCompare(String(b.title || ""), "fr")
    );

    const filtered = initial
      ? sorted.filter((page) => page.id !== initial.id)
      : sorted;

    return root.concat(filtered);
  }, [parents, initial]);

  const updateField = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleCoverSelect = (media) => {
    if (!media?.url) return;

    updateField("cover_image_url", toAbsolutePublicUrl(media.url));
    setMediaOpen(false);
  };

  const buildPayload = () => ({
    locale: form.locale || "fr",
    title: form.title.trim(),
    subtitle: form.subtitle?.trim() || null,
    slug: slugify(form.slug || form.title),
    excerpt: form.excerpt?.trim() || null,
    cover_image_url: form.cover_image_url?.trim() || null,
    parent_id:
      form.parent_id === "" || form.parent_id === null
        ? null
        : Number(form.parent_id),
    template: form.template?.trim() || null,
    redirect_url: form.redirect_url?.trim() || null,
    seo_title: form.seo_title?.trim() || null,
    seo_description: form.seo_description?.trim() || null,
    seo_keywords: form.seo_keywords?.trim() || null,
    is_active: !!form.is_active,
    published_at: form.published_at
      ? new Date(form.published_at).toISOString()
      : null,
  });

  const submit = async (afterSave = "close") => {
    if (!form.title.trim()) {
      setError("Le titre de la page est obligatoire.");
      return;
    }

    if (!slugify(form.slug || form.title)) {
      setError("Le slug est obligatoire.");
      return;
    }

    try {
      setError("");
      setSavingMode(afterSave);

      await onSave(buildPayload(), initial?.id || null, afterSave);
    } catch (submitError) {
      console.error("Erreur PageForm submit:", submitError);
      setError(
        "Erreur lors de l’enregistrement. Le slug existe peut-être déjà."
      );
    } finally {
      setSavingMode("");
    }
  };

  const footer = (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <button
        type="button"
        onClick={onClose}
        disabled={!!savingMode}
        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
      >
        <FiX />
        Annuler
      </button>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
        {isEdit ? (
          <button
            type="button"
            onClick={() => onOpenBuilder?.(initial.id)}
            disabled={!!savingMode}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-bold text-sky-700 transition hover:bg-sky-100 disabled:opacity-50 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-200"
          >
            <FiLayout />
            Modifier le contenu
          </button>
        ) : (
          <button
            type="button"
            onClick={() => submit("builder")}
            disabled={!!savingMode}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-bold text-sky-700 transition hover:bg-sky-100 disabled:opacity-50 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-200"
          >
            <FiLayout />
            {savingMode === "builder"
              ? "Création…"
              : "Créer puis éditer le contenu"}
          </button>
        )}

        <button
          type="button"
          onClick={() => submit("close")}
          disabled={!!savingMode}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-600 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-sky-700 disabled:opacity-50"
        >
          <FiSave />
          {savingMode === "close"
            ? "Enregistrement…"
            : isEdit
            ? "Enregistrer"
            : "Créer la page"}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={isEdit ? "Modifier les informations" : "Nouvelle page"}
        subtitle={
          isEdit
            ? "Modifiez les informations générales de la page. Le contenu se gère dans le builder."
            : "Créez la page, puis ouvrez le builder pour composer son contenu."
        }
        size="lg"
        footer={footer}
      >
        <div className="space-y-6">
          {error ? (
            <div className="rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200">
              {error}
            </div>
          ) : null}

          <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <SectionTitle
              icon={<FiEdit3 />}
              title="Informations principales"
              description="Ces informations structurent la page et son adresse."
            />

            <div className="mt-5 grid gap-4 md:grid-cols-[minmax(0,1fr)_120px]">
              <Field label="Titre de la page *">
                <input
                  value={form.title}
                  onChange={(e) => updateField("title", e.target.value)}
                  className={inputClass}
                  placeholder="Ex : Services municipaux"
                />
              </Field>

              <Field label="Langue">
                <select
                  value={form.locale}
                  onChange={(e) => updateField("locale", e.target.value)}
                  className={inputClass}
                >
                  <option value="fr">fr</option>
                </select>
              </Field>
            </div>

            <div className="mt-4">
              <Field label="Sous-titre">
                <input
                  value={form.subtitle}
                  onChange={(e) => updateField("subtitle", e.target.value)}
                  className={inputClass}
                  placeholder="Optionnel"
                />
              </Field>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
              <Field label="Slug / adresse de la page *">
                <input
                  value={form.slug}
                  onChange={(e) => {
                    updateField("slug", slugify(e.target.value));
                    setAutoSlug(false);
                  }}
                  className={inputClass}
                  placeholder="services-municipaux"
                />
              </Field>

              <label className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={autoSlug}
                  onChange={(e) => setAutoSlug(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Auto
              </label>
            </div>

            <div className="mt-3 rounded-2xl bg-slate-50 px-4 py-3 text-xs text-slate-500 dark:bg-slate-950 dark:text-slate-400">
              Adresse prévue :{" "}
              <code className="font-bold text-slate-700 dark:text-slate-200">
                /pages/{form.slug || "votre-page"}
              </code>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <SectionTitle
              icon={<FiLayout />}
              title="Organisation"
              description="Choisissez une page parente et l’état de publication."
            />

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <Field label="Page parente">
                <select
                  value={form.parent_id ?? ""}
                  onChange={(e) =>
                    updateField(
                      "parent_id",
                      e.target.value === "" ? null : Number(e.target.value)
                    )
                  }
                  className={inputClass}
                >
                  {parentOptions.map((p) => (
                    <option key={String(p.id)} value={p.id ?? ""}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Date de publication">
                <input
                  type="datetime-local"
                  value={form.published_at}
                  onChange={(e) => updateField("published_at", e.target.value)}
                  className={inputClass}
                />
              </Field>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => updateField("is_active", e.target.checked)}
                  className="rounded border-slate-300"
                />
                Page active
              </label>

              <Field label="Template spécifique">
                <input
                  value={form.template}
                  onChange={(e) => updateField("template", e.target.value)}
                  className={inputClass}
                  placeholder="Optionnel"
                />
              </Field>
            </div>

            <div className="mt-4">
              <Field label="Redirection">
                <div className="relative">
                  <FiExternalLink className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                  <input
                    value={form.redirect_url}
                    onChange={(e) =>
                      updateField("redirect_url", e.target.value)
                    }
                    className={`${inputClass} pl-11`}
                    placeholder="/contact ou https://..."
                  />
                </div>
              </Field>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <SectionTitle
              icon={<FiImage />}
              title="Image et résumé"
              description="Utilisés dans les listes, aperçus et partages."
            />

            <div className="mt-5">
              <Field label="Image de couverture">
                <div className="flex gap-2">
                  <input
                    value={form.cover_image_url}
                    onChange={(e) =>
                      updateField("cover_image_url", e.target.value)
                    }
                    className={inputClass}
                    placeholder="/uploads/... ou https://..."
                  />

                  <button
                    type="button"
                    onClick={() => setMediaOpen(true)}
                    className="inline-flex shrink-0 items-center justify-center rounded-2xl border border-sky-200 bg-sky-50 px-4 text-sky-700 transition hover:bg-sky-100 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-200"
                    title="Choisir dans la photothèque"
                  >
                    <FiImage />
                  </button>
                </div>
              </Field>

              {form.cover_image_url ? (
                <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
                  <img
                    src={form.cover_image_url}
                    alt={form.title || ""}
                    className="max-h-64 w-full object-cover"
                  />
                </div>
              ) : null}
            </div>

            <div className="mt-4">
              <Field label="Extrait / résumé">
                <textarea
                  value={form.excerpt}
                  onChange={(e) => updateField("excerpt", e.target.value)}
                  className={textareaClass}
                  rows={4}
                  placeholder="Résumé court pour les listes et aperçus."
                />
              </Field>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <SectionTitle
              icon={<FiExternalLink />}
              title="Référencement"
              description="Informations utiles pour Google et le partage de la page."
            />

            <div className="mt-5 space-y-4">
              <Field label="SEO Title">
                <input
                  value={form.seo_title}
                  onChange={(e) => updateField("seo_title", e.target.value)}
                  className={inputClass}
                  placeholder="Titre optimisé pour les moteurs de recherche"
                />
              </Field>

              <Field label="SEO Description">
                <textarea
                  value={form.seo_description}
                  onChange={(e) =>
                    updateField("seo_description", e.target.value)
                  }
                  className={textareaClass}
                  rows={3}
                  placeholder="Description courte affichée dans les résultats de recherche"
                />
              </Field>

              <Field label="SEO Keywords">
                <input
                  value={form.seo_keywords}
                  onChange={(e) => updateField("seo_keywords", e.target.value)}
                  className={inputClass}
                  placeholder="mairie, services, commune..."
                />
              </Field>
            </div>
          </section>

          {/* <div className="rounded-3xl border border-sky-200 bg-sky-50 p-4 text-sm leading-6 text-sky-800 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-200">
            <strong>Contenu de la page :</strong> le texte, les images, les
            cartes et les boutons sont maintenant gérés uniquement dans le
            builder simplifié. Cela évite aux clients de mélanger les
            informations techniques de la page avec son contenu visuel.
          </div> */}
        </div>
      </Drawer>

      <MediaPickerModal
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        onSelect={handleCoverSelect}
        defaultCategory="pages"
      />
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>
      {children}
    </label>
  );
}

function SectionTitle({ icon, title, description }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-950">
        {icon}
      </div>

      <div>
        <h3 className="font-bold text-slate-950 dark:text-slate-50">
          {title}
        </h3>

        {description ? (
          <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
}